"use client"
import { create } from "zustand"

interface StateSharedData {
  states: any[],
  state: any,
  restaurantsByState: any[],
  hotelsByState: any[],
  error: string | null,
  loading: boolean,

  fetchStates: () => Promise<void>,
  fetchState: (slug: string) => Promise<void>,
  fetchRestaurantsByState: (slug: string) => Promise<void>,
  fetchHotelsByState: (slug: string) => Promise<void>,
  fetchStateFull: (slug: string) => Promise<void>,
}

export const useStateSharedData = create<StateSharedData>((set) => ({
  states: [],
  state: null,
  restaurantsByState: [],
  hotelsByState: [],
  error: null,
  loading: false,

  //------------------------------------
  // 📌 Fetch all states
  //------------------------------------
  fetchStates: async () => {
    set({ loading: true, error: null })
    try {
      const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}states`)
      if (!res.ok) throw new Error("Failed to fetch states")

      const data = await res.json()
      set({ states: data.states })
    } catch (err: any) {
      set({ error: err.message })
    } finally {
      set({ loading: false })
    }
  },

  //------------------------------------
  // 📌 Fetch ONE state
  //------------------------------------
  fetchState: async (slug: string) => {
    set({ loading: true, error: null })
    try {
      const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}states/${slug}`)
      if (!res.ok) throw new Error("Failed to fetch state")

      const data = await res.json()
      set({ state: data.state })
    } catch (err: any) {
      set({ error: err.message })
    } finally {
      set({ loading: false })
    }
  },

  //------------------------------------
  // 📌 Fetch Restaurants by State
  //------------------------------------
  fetchRestaurantsByState: async (slug: string) => {
    set({ loading: true, error: null })
    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}restaurantsbystate/${slug}`
      )
      if (!res.ok) throw new Error("Failed to fetch restaurants")

      const data = await res.json()
      set({ restaurantsByState: data.restaurants.data })
    } catch (err: any) {
      set({ error: err.message })
    } finally {
      set({ loading: false })
    }
  },

  //------------------------------------
  // 📌 Fetch Hotels by State
  //------------------------------------
  fetchHotelsByState: async (slug: string) => {
    set({ loading: true, error: null })
    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}hotelsbystate/${slug}`
      )
      if (!res.ok) throw new Error("Failed to fetch hotels")

      const data = await res.json()
      set({ hotelsByState: data.hotels.data })
    } catch (err: any) {
      set({ error: err.message })
    } finally {
      set({ loading: false })
    }
  },

  //------------------------------------
  // ⭐ Fetch STATE + RESTAURANTS + HOTELS TOGETHER
  //------------------------------------
  fetchStateFull: async (slug: string) => {
    set({ loading: true, error: null })

    try {
      const [stateRes, restaurantsRes, hotelsRes] = await Promise.all([
        fetch(`${process.env.NEXT_PUBLIC_API_URL}states/${slug}`),
        fetch(`${process.env.NEXT_PUBLIC_API_URL}restaurantsbystate/${slug}`),
        fetch(`${process.env.NEXT_PUBLIC_API_URL}hotelsbystate/${slug}`),
      ])

      if (!stateRes.ok) throw new Error("Failed to fetch state")
      if (!restaurantsRes.ok) throw new Error("Failed to fetch restaurants")
      if (!hotelsRes.ok) throw new Error("Failed to fetch hotels")

      const stateData = await stateRes.json()
      const restaurantsData = await restaurantsRes.json()
      const hotelsData = await hotelsRes.json()

      set({
        state: stateData.state,
        restaurantsByState: restaurantsData.restaurants,
        hotelsByState: hotelsData.hotels,
      })
    } catch (err: any) {
      set({ error: err.message })
    } finally {
      set({ loading: false })
    }
  },
}))
